import { useDispatch, useSelector } from 'react-redux';
import { useEffect } from 'react';
import { useCurrentCourse } from '@reducers/courses/hooks';
import {
  actions as CurriculumActions,
  getCurriculumById,
  isCurriculumLoading,
  selectors as CurriculumSelectors,
} from './curriculumsReducer';

export type CurriculumSubject = {
  id: number;
  display_name: string;
  public?: boolean;
};

export type CurriculumDetails = {
  id: number;
  name: string;
  slug: string;
  public: boolean;
  has_exam: boolean;
  available_subjects: CurriculumSubject[];
  adaptive_learnosity_practice_enabled: boolean;
  learnosity_lesson_review_enabled: boolean;
};

type RootState = {
  curriculums: unknown;
  // Other slices exist but aren't needed here.
};

// eslint-disable-next-line import/prefer-default-export
export const useCurriculumDetails = (): CurriculumDetails | Record<string, never> => {
  const courseDetails = useCurrentCourse();
  const curriculumDetails = useSelector((state: RootState) =>
    getCurriculumById(state, courseDetails.curriculum),
  );

  return curriculumDetails;
};

/**
 * Whether the curriculums slice currently has a fetch in flight. `getCurriculumById`
 * returns `{}` both for a curriculum that is absent and for one that has not loaded yet,
 * so callers that must tell those apart need this alongside the data.
 */
export const useIsCurriculumLoading = (): boolean =>
  useSelector((state: RootState) => isCurriculumLoading(state)) as boolean;

export const useIsAdaptiveLearnosityPracticeEnabled = (): boolean => {
  const currentCurriculumDetails = useCurriculumDetails();
  return currentCurriculumDetails?.adaptive_learnosity_practice_enabled ?? false;
};

export const useIsLearnosityLessonReviewEnabled = (): boolean => {
  const currentCurriculumDetails = useCurriculumDetails();
  return currentCurriculumDetails?.learnosity_lesson_review_enabled ?? false;
};

type UseAvailableCurriculumsOptions = {
  enabled?: boolean;
};

export const useAvailableCurriculums = (options?: UseAvailableCurriculumsOptions) => {
  const dispatch = useDispatch();
  const enabled = options?.enabled ?? true;

  const curriculums = useSelector((state: RootState) =>
    CurriculumSelectors.getAllCurriculums(state),
  );
  const isLoading = useSelector((state: RootState) =>
    CurriculumSelectors.isCurriculumLoading(state),
  );
  const error = useSelector((state: RootState) =>
    CurriculumSelectors.getCurriculumErrorMessage(state),
  );

  useEffect(() => {
    if (!enabled) {
      return;
    }
    if ((!curriculums || curriculums.length === 0) && !isLoading) {
      dispatch(CurriculumActions.getCurriculumList());
    }
  }, [curriculums, dispatch, enabled, isLoading]);

  return {
    curriculums: (curriculums || []) as CurriculumDetails[],
    isLoading: Boolean(isLoading),
    error,
    refetch: () => dispatch(CurriculumActions.getCurriculumList()),
  };
};
